import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthProvider';
import { AdminConfigProvider, useAdminConfig } from './contexts/AdminConfigProvider';
import { ToastProvider } from './contexts/ToastProvider';
import Dashboard from './pages/Dashboard';
import Tickets from './pages/Tickets';
import Calendar from './pages/Calendar';
import UserSettings from './pages/UserSettings';
import SystemSettings from './pages/SystemSettings';
import FeedbackAdmin from './pages/FeedbackAdmin';
import AdminSettings from './pages/AdminSettings';
import BidJobAdmin from './pages/BidJobAdmin';
import ContactApprovalsAdmin from './pages/ContactApprovalsAdmin';
import TechUserManagement from './pages/TechUserManagement';
import Reports from './pages/Reports';
import FieldMap from './pages/FieldMap';
import Embedded from './pages/Embedded';
import IntakeForm from './pages/IntakeForm';
import FloatingDebugPanel from './components/FloatingDebugPanel';
import { ErrorBoundary } from './components/ErrorBoundary';
import InstallPrompt from './components/InstallPrompt';
import { useLocationTracking } from './hooks/useLocationTracking';
import { getCurrentResourceId } from './utils/featureFlags';
import { getUserNotificationSettings, getResources } from './services/api';

// Protected route wrapper for Reports - uses admin list from database
function ProtectedReportsRoute() {
  const { user } = useAuth();
  const { isAdmin, isLoading } = useAdminConfig();

  // Wait for admin config to load
  if (isLoading) {
    return (
      <main className="flex-1 px-4 pb-4 overflow-auto flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </main>
    );
  }

  if (!isAdmin(user?.email)) {
    return <Navigate to="/" replace />;
  }

  return (
    <main className="flex-1 px-4 pb-4 overflow-auto">
      <Reports />
    </main>
  );
}

function AppRoutes() {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  // Read theme from URL parameter (sent by Mission Control HUD)
  const [theme, setTheme] = useState<'dark' | 'light'>(() => {
    // Check URL param first, then localStorage, default to dark
    const urlTheme = new URLSearchParams(window.location.search).get('theme');
    if (urlTheme === 'light' || urlTheme === 'dark') return urlTheme;
    const saved = localStorage.getItem('autotask-theme');
    return (saved === 'light' ? 'light' : 'dark');
  });

  // App-wide location tracking
  const [locationResourceId, setLocationResourceId] = useState<number | null>(null);
  const [locationResourceName, setLocationResourceName] = useState<string | undefined>();
  const [locationConsent, setLocationConsent] = useState(false);
  const [locationWorkHoursStart, setLocationWorkHoursStart] = useState(7);
  const [locationWorkHoursEnd, setLocationWorkHoursEnd] = useState(18);

  useEffect(() => {
    // Auto-match logged-in user to AutoTask resource, then load settings
    getResources().catch((): never[] => []).then((allResources) => {
      // If user is logged in, auto-match by email
      if (user?.email && user.email !== 'embed@hud.local') {
        const matched = allResources.find(
          r => r.email?.toLowerCase() === user.email.toLowerCase()
        );
        if (matched) {
          const currentId = getCurrentResourceId();
          if (currentId !== matched.autoTaskId) {
            localStorage.setItem('autotask-hub-resource-id', String(matched.autoTaskId));
          }
          setLocationResourceId(matched.autoTaskId);
          setLocationResourceName(`${matched.firstName} ${matched.lastName}`);
        }
      }

      // Load notification settings for the resolved resource
      const resourceId = getCurrentResourceId();
      if (!resourceId) return;
      if (!locationResourceId) setLocationResourceId(resourceId);

      // Find resource name if not already set
      if (!locationResourceName) {
        const resource = allResources.find(r => r.autoTaskId === resourceId);
        if (resource) {
          setLocationResourceName(`${resource.firstName} ${resource.lastName}`);
        }
      }

      getUserNotificationSettings(resourceId).catch(() => null).then((settings) => {
        if (settings) {
          setLocationConsent(settings.locationConsent ?? false);
          setLocationWorkHoursStart(settings.workHoursStart ?? 7);
          setLocationWorkHoursEnd(settings.workHoursEnd ?? 18);
        }
      });
    });
  }, [user?.email]);

  // Listen for changes to location consent (from UserSettings page)
  useEffect(() => {
    function handleStorageChange() {
      const resourceId = getCurrentResourceId();
      if (!resourceId) return;

      // Re-fetch settings when resource changes
      if (resourceId !== locationResourceId) {
        setLocationResourceId(resourceId);
        getUserNotificationSettings(resourceId).then(settings => {
          setLocationConsent(settings.locationConsent ?? false);
          setLocationWorkHoursStart(settings.workHoursStart ?? 7);
          setLocationWorkHoursEnd(settings.workHoursEnd ?? 18);
        }).catch(() => {});
      }
    }
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [locationResourceId]);

  useLocationTracking({
    resourceId: locationResourceId,
    resourceName: locationResourceName,
    enabled: locationConsent,
    workHoursStart: locationWorkHoursStart,
    workHoursEnd: locationWorkHoursEnd,
  });

  // Update theme when URL param changes (from HUD toggle)
  useEffect(() => {
    const urlTheme = searchParams.get('theme');
    if (urlTheme === 'light' || urlTheme === 'dark') {
      setTheme(urlTheme);
      localStorage.setItem('autotask-theme', urlTheme);
    }
  }, [searchParams]);

  // Apply theme to document
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  return (
    <div className="h-screen bg-slate-900 text-slate-100 flex flex-col overflow-hidden">
      <Routes>
        {/* Calendar page - full height, no padding, no scroll */}
        <Route path="/calendar" element={<main className="flex-1 overflow-hidden"><ErrorBoundary routeName="Calendar"><Calendar /></ErrorBoundary></main>} />
        {/* Other pages - with padding, allow scroll */}
        <Route path="/" element={<main className="flex-1 px-4 pb-4 overflow-auto"><Dashboard /></main>} />
        <Route path="/tickets" element={<main className="flex-1 px-4 pb-4 overflow-auto"><Tickets /></main>} />
        <Route path="/reports" element={<ProtectedReportsRoute />} />
        <Route path="/settings" element={<Navigate to="/settings/user" replace />} />
        <Route path="/settings/user" element={<main className="flex-1 px-4 pb-4 overflow-auto"><UserSettings /></main>} />
        <Route path="/settings/system" element={<main className="flex-1 px-4 pb-4 overflow-auto"><SystemSettings /></main>} />
        <Route path="/settings/feedback" element={<main className="flex-1 px-4 pb-4 overflow-auto"><FeedbackAdmin /></main>} />
        <Route path="/settings/admin" element={<main className="flex-1 px-4 pb-4 overflow-auto"><AdminSettings /></main>} />
        <Route path="/settings/bidjobs" element={<main className="flex-1 px-4 pb-4 overflow-auto"><BidJobAdmin /></main>} />
        <Route path="/settings/contact-approvals" element={<main className="flex-1 px-4 pb-4 overflow-auto"><ContactApprovalsAdmin /></main>} />
        <Route path="/settings/techs" element={<main className="flex-1 px-4 pb-4 overflow-auto"><TechUserManagement /></main>} />
        <Route path="/field-map" element={<main className="flex-1 px-4 pb-4 overflow-auto"><FieldMap /></main>} />
        <Route path="/embedded" element={<main className="flex-1 px-4 pb-4 overflow-auto"><Embedded /></main>} />
      </Routes>
      <InstallPrompt resourceId={locationResourceId} />
    </div>
  );
}

function AppContent() {
  const { isLoading, isAuthenticated, isEmbedMode, login } = useAuth();

  // Public intake form route - no auth required
  if (window.location.pathname.startsWith('/intake/')) {
    return (
      <Routes>
        <Route path="/intake/:token" element={<IntakeForm />} />
      </Routes>
    );
  }

  // Show loading spinner while checking auth
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // In embed mode (iframe from HUD), always allow - HUD handles auth
  if (isEmbedMode) {
    return <AppRoutes />;
  }

  // For direct access, require authentication
  if (!isAuthenticated) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-slate-900 text-white">
        <h1 className="text-2xl font-bold mb-4">Authentication Required</h1>
        <p className="mb-6 text-slate-400">Please sign in to access AutoTask Hub</p>
        <button
          onClick={login}
          className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Sign in with Microsoft
        </button>
      </div>
    );
  }

  return <AppRoutes />;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AdminConfigProvider>
          <ToastProvider>
            <AppContent />
            {/* Floating Debug Panel - always visible, even over modals */}
            <FloatingDebugPanel />
          </ToastProvider>
        </AdminConfigProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
